import { unified, type Processor } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeKatex from 'rehype-katex';
import rehypeStringify from 'rehype-stringify';
import rehypeShiki from '@shikijs/rehype';
import rehypeSlug from 'rehype-slug';
import type { Root as MdastRoot } from 'mdast';
import type { Root as HastRoot } from 'hast';
import { sanitizeSchema } from './sanitizeSchema';
import { rehypeEmbeds } from './rehypeEmbeds';
import { rehypeSourceLines } from './rehypeSourceLines';

/** Shiki themes matched to the app's light/dark mode (spec §6). */
const SHIKI_THEMES = { light: 'github-light', dark: 'github-dark' } as const;

/**
 * A fresh unified processor configured through the sanitize boundary
 * (spec §4). `remark-math` runs at the mdast stage (before remark-rehype) so
 * math nodes survive into hast; `rehypeSourceLines` runs right after
 * remark-rehype (while mdast positions are intact, before rehype-raw re-parses)
 * to stamp `data-line` on block elements; everything AFTER sanitize (KaTeX,
 * Shiki, embed-rewrite, the final compiler) is layered on by `corePipeline`
 * callers — sanitize runs FIRST so the trusted generators' markup is never
 * stripped.
 *
 * Returned unfrozen so callers can `.use(...)` additional plugins.
 */
export function corePipeline(): Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined> {
  return unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeSourceLines)
    .use(rehypeRaw)
    .use(rehypeSanitize, sanitizeSchema) as unknown as Processor<
    MdastRoot,
    MdastRoot,
    HastRoot,
    undefined,
    undefined
  >;
}

/**
 * Extend `corePipeline` with the trusted, post-sanitize generators in the
 * security-critical order (spec §4):
 *
 *   …sanitize boundary…
 *     → rehype-slug    (stable `id` slugs on headings; trusted, post-sanitize)
 *     → rehypeEmbeds   (rewrite mermaid/vega code → `div.mp-embed`; BEFORE Shiki)
 *     → rehype-katex   (math → KaTeX markup; trust:false, strict:false)
 *     → @shikijs/rehype (fenced-code highlighting; theme-matched, async/lazy)
 *
 * `rehype-slug` runs AFTER the sanitize boundary so the generated `id`s are
 * trusted and survive — these match `extractHeadings` slugs (both use a single
 * GithubSlugger over headings in document order) for anchor navigation.
 *
 * Shiki loads themes/grammars on demand (`lazy:true`) so the pipeline is async;
 * callers must `await processor.process(text)`. `inline:false` keeps inline
 * code plain. Embeds run first so Shiki skips them.
 */
export function trustedPipeline(
  opts: { dark?: boolean } = {},
): Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined> {
  return corePipeline()
    .use(rehypeSlug)
    .use(rehypeEmbeds())
    .use(rehypeKatex, { trust: false, strict: false })
    .use(rehypeShiki, {
      themes: SHIKI_THEMES,
      defaultColor: opts.dark ? 'dark' : 'light',
      lazy: true,
      inline: false,
      // Fence labels Shiki doesn't ship as registered grammars but users
      // commonly write. Without these `@shikijs/rehype` throws
      // "Language `<x>` is not included in this bundle".
      langAlias: { math: 'tex', latex: 'tex', plaintext: 'text', txt: 'text' },
    }) as unknown as Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined>;
}

/**
 * Cached processors. unified processors are safe to reuse across calls once
 * frozen by `.parse()` / `.run()` — they hold no per-document state — and the
 * plugin chain is non-trivial to build (~12 `.use()` calls). Reusing saves
 * 10–30 ms per call on small docs (negligible on 50K-line docs vs. parse cost
 * but still free). The trusted processor is per-`dark` because the Shiki
 * default-color is baked into the plugin config.
 */
const trustedProcessorByDark = new Map<
  boolean,
  Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined>
>();
let coreProcessor: Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined> | null = null;

function getTrustedProcessor(
  dark: boolean,
): Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined> {
  let p = trustedProcessorByDark.get(dark);
  if (!p) {
    p = trustedPipeline({ dark });
    trustedProcessorByDark.set(dark, p);
  }
  return p;
}

function getCoreProcessor(): Processor<MdastRoot, MdastRoot, HastRoot, undefined, undefined> {
  if (!coreProcessor) coreProcessor = corePipeline();
  return coreProcessor;
}

/**
 * Run markdown through the full trusted pipeline (sanitize → embeds → KaTeX →
 * Shiki) and return the resulting hast tree. ASYNC because Shiki lazily loads
 * themes/grammars. The preview (in `@slate/ui`) hydrates this tree to React via
 * `rehype-react`; export serializes it via `rehype-stringify`.
 */
export async function renderToTrustedHast(
  markdown: string,
  opts: { dark?: boolean } = {},
): Promise<HastRoot> {
  const processor = getTrustedProcessor(!!opts.dark);
  const mdast = processor.parse(markdown);
  return (await processor.run(mdast)) as HastRoot;
}

/** Render markdown to a sanitized HTML string (used for export and tests). */
export function renderToHtml(markdown: string): string {
  // Not cached: `.use(rehypeStringify)` mutates the processor's plugin chain,
  // so this path needs a fresh processor each call.
  const file = corePipeline().use(rehypeStringify).processSync(markdown);
  return String(file);
}

/** Render markdown to a sanitized hast tree (for worker transport / inspection). */
export function renderToHast(markdown: string): HastRoot {
  const processor = getCoreProcessor();
  return processor.runSync(processor.parse(markdown));
}
